/**
 * The capture domain's face (ADR 0014).
 *
 * Every way an entry arrives: AI analysis of the user's own text, external HTTP ingest, the in-app
 * form, pending captures, voice. Catalogue lookups are `enrichment/`'s and are not here — capture
 * calls them, which is the legal direction.
 *
 * Every file outside `src/capture/` reaches this domain through this module (Rule D). Files inside
 * use relative paths.
 *
 * **Explicit named exports, never `export *`** (ADR 0014:81-82). A name reaches this list because a
 * caller outside the domain imports it.
 *
 * `capture/` is step 4's single permitted partial domain (#153), for the life of the
 * `step-4-target-structure` branch only: `document/` finished at step 1 and `entries/` at step 2, so
 * one domain may be under construction. The sub-modules land beneath this face in the order #361
 * cut them.
 *
 * **This face is the client-safe half; `capture/server/index.ts` is the other** (ADR 0014:23 lists
 * both). The split is not tidiness: `ingest/handleCapture.ts` names `after` from `next/server`, and
 * a single face put it in the client graph of every component that imports capture — `pnpm build`
 * fails with "You're importing a module that depends on 'after'", and no earlier gate says a word.
 * ADR 0014 records the converse hazard, a server-only re-export poisoning client consumers; this is
 * the same seam from the other side.
 *
 * The rule for adding a name here: if a client component can reach it, it belongs on this face and
 * the module it comes from carries `'use client'` where it calls a hook. Everything a route calls
 * belongs on the server face.
 *
 * @see CONTEXT.md for what capture means
 */

// ============================================================================
// The vocabulary every way in shares
// ============================================================================

export type { CaptureAnalysis, CaptureEntryType, PendingCapture } from './types'
export type { PendingCaptureWithUrl } from './pending/store'

// ============================================================================
// AI analysis of the user's own text
//
// `handleAnalyzeEntry` is the route body ADR 0010 took out of
// `app/api/analyze-entry/route.ts`; `detectEntry` and `extractFromScreenshot` are what the external
// ingest route calls directly.
// ============================================================================

export { useEntryAnalysis } from './analysis/useEntryAnalysis'
export type { AnyAnalysis } from './analysis/useEntryAnalysis'
export {
  ANALYSIS_ENTRY_TYPES,
  TASK_CATEGORIES,
  TASK_PRIORITIES,
} from './analysis/types'
export type {
  AnalysisEntryType,
  AnalysisResult,
  ArtistAnalysis,
  BookAnalysis,
  JournalAnalysis,
  LabelAnalysis,
  MoodScore,
  MovieAnalysis,
  PeopleDetection,
  ReleaseAnalysis,
  TaskAnalysis,
  TaskCategory,
  TaskPriority,
} from './analysis/types'

// ============================================================================
// Voice
//
// `mintRealtimeToken` is the route body ADR 0010 took out of `app/api/realtime-token/route.ts`.
// The recorder has a consumer outside capture — `entries/journal/journal-prompt.tsx` borrows it —
// which is a legal direction only because it is published here rather than duplicated there.
// ============================================================================

export { useStreamingVoiceRecorder } from './voice/useStreamingVoiceRecorder'
export { VoiceInputOverlay } from './voice/voice-input-overlay'
export { VoiceVisualizer } from './voice/voice-visualizer'

// ============================================================================
// Pending captures
//
// Where a capture waits between arriving over HTTP and reaching the document. The two handlers are
// the route bodies ADR 0010 took out of `app/api/pending-entries/route.ts`; `storePendingCapture`
// is what the ingest route calls when it parks one.
// ============================================================================

// ============================================================================
// The media preview card
//
// The confirmation step of the in-app form: preview a chosen catalogue result, edit it, confirm —
// before the entry exists. Out of `components/library/`, where it had no library consumer (#405).
// ============================================================================

export { MediaPreviewCard } from './media-preview/card'
export type {
  LabelSuggestion,
  MediaEditValues,
  Person,
} from './media-preview/types'

// ============================================================================
// The in-app form, and the surfaces over it
//
// Two shells over one hook stack, and naming that is part of what the move buys. `PromptCaptureBox`
// is the shipped surface, mounted through `PromptCaptureModal` at `app/app/layout-client.tsx`;
// `UnifiedForm` is the older one, live only through `components/projects/project-detail.tsx`.
// Collapsing them is a product decision, not a move, so both are published.
//
// `TopicSubtitleSelector` is the second name `entries/` borrows from this domain, after the voice
// recorder — `entries/journal/row.tsx`, `entries/task/row.tsx` and `entries/task/edit-form.tsx`
// each render one. Legal only through this face. It also reads more like `topics/` vocabulary than
// like capture's; step 5 creates that domain and can take the question up.
// ============================================================================

export { PromptCaptureBox } from './prompt-capture-box'
export { PromptCaptureModal } from './prompt-capture-modal'
export { UnifiedForm } from './unified-form'
export { TopicSubtitleSelector } from './forms/TopicSubtitleSelector'

// ============================================================================
// The ingest seam
//
// External HTTP and the in-app form, as two adapters onto one `EntryDraft` rather than two
// implementations of "turn an analysis result into an entry" (ADR 0009's third real seam).
// `handleCapture` is the route body ADR 0010 took out of `app/api/capture/route.ts`;
// `EntryUpdateProvider` is the browser half that writes what arrives.
// ============================================================================

export {
  EntryUpdateProvider,
  useEntryUpdates,
} from './ingest/EntryUpdateProvider'
export { buildMediaCreateData } from './ingest/from-analysis'
export type { MediaAnalysis, MediaCreateData } from './ingest/from-analysis'
